if (!customElements.get('workshop-gift-purchase')) {
  customElements.define('workshop-gift-purchase', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-workshop-variants]').textContent);
      this.form = this.querySelector('product-form');
      this.onVariantChange = this.onVariantChange || this.selectVariant.bind(this);
      this.addEventListener('change', this.onVariantChange);
      this.onQuantityChange = this.onQuantityChange || this.syncQuantity.bind(this);
      this.addEventListener('input', this.onQuantityChange);
      this.addEventListener('change', this.onQuantityChange);
      this.sticky = this.querySelector('[data-workshop-sticky]');
      if (this.sticky) this.connectStickyCart();
      this.updateTotal();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onVariantChange);
      this.removeEventListener('input', this.onQuantityChange);
      this.removeEventListener('change', this.onQuantityChange);
      this.stickyObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.cardObserver?.disconnect();
      this.stickySizeObserver?.disconnect();
      if (this.sticky) document.documentElement.style.removeProperty('--workshop-sticky-height');
      this.cartErrorUnsubscribe?.();
    }

    selectVariant(event) {
      let input = event.target;
      if (input.matches('[data-workshop-sticky-variant]')) {
        input = this.querySelector(`[data-workshop-variant][value="${input.value}"]`);
        if (!input) throw new Error('Workshop gift purchase: sticky variant has no matching card.');
        input.checked = true;
      } else if (!input.matches('[data-workshop-variant]')) {
        return;
      }

      const variant = this.variants.find((item) => String(item.id) === input.value);
      if (!variant || !this.form) {
        throw new Error('Workshop gift purchase: variant or native product form missing.');
      }

      const variantInput = this.form.querySelector('[name="id"]');
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      const quantity = this.querySelector('[name="quantity"]');
      quantity.min = input.dataset.quantityMin;
      quantity.step = input.dataset.quantityStep;
      if (input.dataset.quantityMax) {
        quantity.max = input.dataset.quantityMax;
      } else {
        quantity.removeAttribute('max');
      }
      if (!quantity.validity.valid) quantity.value = quantity.min;
      this.updateStickyVariant(variant, input);
      this.updateTotal();

      const button = this.form.querySelector('[name="add"]');
      button.disabled = !variant.available;
      button.textContent = variant.available ? this.dataset.addToCartLabel : theme.variantStrings.soldOut;
      this.form.handleErrorMessage();
      this.syncStickyButton();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url);

      // The native recipient component tracks its variant through this theme event.
      publish(PUB_SUB_EVENTS.variantChange, {
        data: { sectionId: this.dataset.section, variant }
      });
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    connectStickyCart() {
      this.stickyObserver = new MutationObserver(() => this.syncStickyVisibility());
      this.stickyObserver.observe(this.sticky, { attributes: true, attributeFilter: ['animate'] });
      this.stickyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.cardObserver = new ResizeObserver(() => this.sticky.onScroll());
      this.cardObserver.observe(this.querySelector('.workshop-gift__card'));
      this.stickySizeObserver = new ResizeObserver(() => this.syncBottomSpace());
      this.stickySizeObserver.observe(this.sticky.querySelector('.workshop-gift__sticky-cart'), { box: 'border-box' });
      this.buttonObserver = new MutationObserver(() => this.syncStickyButton());
      this.buttonObserver.observe(this.form.querySelector('[name="add"]'), {
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true
      });
      this.cartErrorUnsubscribe = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event.source !== 'product-form' || String(event.productVariantId) !== this.form.querySelector('[name="id"]').value) return;
        // Let the native recipient/form subscribers render the error before focusing it.
        queueMicrotask(() => {
          if (!this.isConnected) return;
          const error = this.form.querySelector('.product-form__recipient-error-message-wrapper:not([hidden]) .form__message')
            || this.form.querySelector('.product-form__error-message-wrapper:not([hidden])');
          if (!error) return;
          error.tabIndex = -1;
          error.scrollIntoView({ block: 'center' });
          error.focus({ preventScroll: true });
        });
      });
      this.syncStickyVisibility();
      this.syncStickyButton();
    }

    syncStickyVisibility() {
      const overlayOpen = /(?:mini-cart|menu-drawer|menu-mobile|search-modal|quick-view)--(?:open|opening|closing)/.test(document.body.className);
      const visible = this.sticky.hasAttribute('animate') && !overlayOpen;
      this.sticky.inert = !visible;
      this.sticky.setAttribute('aria-hidden', String(!visible));
      this.syncBottomSpace();
    }

    syncBottomSpace() {
      const visible = !this.sticky.inert;
      const height = visible ? this.sticky.querySelector('.workshop-gift__sticky-cart').getBoundingClientRect().height : 0;
      const value = `${height}px`;
      if (document.documentElement.style.getPropertyValue('--workshop-sticky-height') !== value) {
        document.documentElement.style.setProperty('--workshop-sticky-height', value);
      }
    }

    syncStickyButton() {
      if (!this.sticky) return;
      const main = this.form.querySelector('[name="add"]');
      const button = this.sticky.querySelector('[data-workshop-sticky-submit]');
      const loading = main.classList.contains('loading');
      button.disabled = main.disabled || loading;
      button.classList.toggle('loading', loading);
      button.setAttribute('aria-disabled', String(button.disabled));
      button.textContent = main.textContent;
    }

    updateStickyVariant(variant, input) {
      if (!this.sticky) return;
      const select = this.sticky.querySelector('[data-workshop-sticky-variant]');
      if (select) select.value = String(variant.id);
      const image = this.sticky.querySelector('[data-workshop-sticky-image]');
      if (image && input.dataset.workshopImage) {
        image.src = input.dataset.workshopImage;
        image.removeAttribute('srcset');
      }
      const quantity = this.querySelector('[name="quantity"]');
      const stickyQuantity = this.sticky.querySelector('[data-workshop-sticky-quantity]');
      if (stickyQuantity) {
        stickyQuantity.min = quantity.min;
        stickyQuantity.step = quantity.step;
        if (quantity.hasAttribute('max')) stickyQuantity.max = quantity.max;
        else stickyQuantity.removeAttribute('max');
        stickyQuantity.value = quantity.value;
      }
    }

    syncQuantity(event) {
      const main = this.querySelector('[name="quantity"]');
      const sticky = this.sticky?.querySelector('[data-workshop-sticky-quantity]');
      if (event.target === sticky) {
        main.value = sticky.value;
        main.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (event.target === main) {
        if (sticky) sticky.value = main.value;
        this.updateTotal();
      }
    }

    updateTotal() {
      const input = this.querySelector('[data-workshop-variant]:checked');
      const variant = this.variants.find((item) => String(item.id) === input.value);
      if (!variant) throw new Error('Workshop gift purchase: selected variant missing for total.');
      const quantity = this.querySelector('[name="quantity"]');
      if (this.totalOverflow) quantity.setCustomValidity('');
      const total = variant.price * quantity.valueAsNumber;
      this.totalOverflow = quantity.validity.valid && !Number.isSafeInteger(total);
      if (this.totalOverflow) quantity.setCustomValidity(this.dataset.invalidQuantityLabel);
      this.querySelectorAll('[data-workshop-price], [data-workshop-sticky-price]').forEach((destination) => {
        if (!quantity.validity.valid) {
          destination.textContent = this.dataset.invalidQuantityLabel;
          destination.classList.add('workshop-gift__price--invalid');
        } else {
          destination.innerHTML = `<bdi>${theme.Currency.formatMoney(total, this.dataset.moneyFormat)}</bdi>`;
          destination.classList.remove('workshop-gift__price--invalid');
        }
      });
    }
  });
}
